import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, DispatchBoardEntry, ServiceResource } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-service-dispatch-board',
  imports: [AinPageComponent, AinStateComponent, RouterLink, ButtonModule],
  template: `
    <ain-page [title]="t('catalog.dispatch.title')" [description]="t('catalog.dispatch.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <div class="toolbar">
          <a [routerLink]="['/operations/control-room']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
        </div>

        <p class="hint">{{ t('catalog.dispatch.dragHint') }}</p>

        <section class="board">
          <article class="column unassigned">
            <h4>{{ t('catalog.dispatch.unassigned') }}</h4>
            @for (row of unassignedOrders(); track row.orderId) {
              <div class="card" draggable="true" [class.breach]="row.isSlaBreached" (dragstart)="onDragStart(row.orderId)">
                <strong>{{ row.orderNumber }}</strong>
                <span>{{ row.title }}</span>
                <small>{{ row.scheduledStartUtc || '-' }}</small>
              </div>
            }
          </article>

          @for (resource of resources(); track resource.id) {
            <article class="column" (dragover)="onDragOver($event)" (drop)="onDrop(resource.id, $event)">
              <h4>{{ resource.code }}</h4>
              <p>{{ resource.name }}</p>
              @for (row of ordersForResource(resource.id); track row.orderId) {
                <div class="card" draggable="true" [class.breach]="row.isSlaBreached" (dragstart)="onDragStart(row.orderId)">
                  <strong>{{ row.orderNumber }}</strong>
                  <span>{{ row.title }}</span>
                  <small>{{ row.scheduledStartUtc || '-' }}</small>
                </div>
              }
            </article>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; gap: 10px; align-items: center; margin-bottom: 12px; }
    .toolbar a { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .hint { color: #64748b; margin: 0 0 12px; }
    .board { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); align-items: start; }
    .column { border: 1px solid #e2e8f0; border-radius: 12px; padding: 10px; min-height: 180px; background: #f8fafc; }
    .column.unassigned { background: #fff; }
    .column h4 { margin: 0 0 4px; }
    .column p { margin: 0 0 8px; color: #64748b; font-size: 12px; }
    .card { border: 1px solid #cbd5e1; border-radius: 10px; padding: 8px; margin-bottom: 8px; background: #fff; cursor: grab; display: grid; gap: 4px; }
    .card.breach { border-color: #fca5a5; background: #fef2f2; }
    .card strong { font-size: 13px; }
    .card span { font-size: 12px; color: #334155; }
    .card small { font-size: 11px; color: #64748b; }
  `]
})
export class ServiceDispatchBoardComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly rows = signal<DispatchBoardEntry[]>([]);
  readonly resources = signal<ServiceResource[]>([]);
  private draggedOrderId = '';

  readonly unassignedOrders = computed(() =>
    this.rows().filter(row => row.assignments.length === 0)
  );

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.service.listServiceResources().subscribe({
      next: resources => this.resources.set(resources.filter(r => r.isActive))
    });
    this.service.getDispatchBoard().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  ordersForResource(resourceId: string): DispatchBoardEntry[] {
    return this.rows().filter(row => row.assignments.some(a => a.resourceId === resourceId));
  }

  onDragStart(orderId: string): void {
    this.draggedOrderId = orderId;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onDrop(resourceId: string, event: DragEvent): void {
    event.preventDefault();
    if (!this.draggedOrderId) return;

    this.service.reassignDispatchBoard(this.draggedOrderId, resourceId, true).subscribe({
      next: () => this.load(),
      error: () => this.load()
    });
    this.draggedOrderId = '';
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
